import { makeStyles } from '@material-ui/styles';
import { differenceInMinutes } from 'date-fns';

import { Text, MainButton } from '@bizzabo/design-system';

import ScheduleModal from '../components/ScheduleModal';
import useTypeTracking from '../../../analytics';
import { useMeetings, View } from '../MeetingsTool/MeetingsToolContext';
import useDeclineMeeting from '../hooks/useDeclineMeeting';
import useOtherUser from '../hooks/useOtherUser';
import { ScheduledMeeting } from '../types';
import LoaderSpinner from '../../../people/components/LoaderSpinner';

interface DeclineMeetingModalProps {
    isOpen: boolean;
    meeting: ScheduledMeeting;
    closeModal: () => void;
}

const DeclineMeetingModal: React.FC<DeclineMeetingModalProps> = ({
    isOpen,
    meeting,
    closeModal,
}) => {
    const classes = useStyles();
    const durationInMinutes = differenceInMinutes(meeting.endDate, meeting.startDate);
    const { eventId, setView } = useMeetings();
    const declineMeeting = useDeclineMeeting();
    const { data: otherUser } = useOtherUser(meeting);

    const { trackClickEvent, Track } = useTypeTracking({
        data: {
            description: {
                meeting_id: meeting.id,
                locationType: meeting.locationType,
                meeting_length: durationInMinutes,
                number_of_invitees: meeting.participants.length - 1,
                invitation_text: meeting.description,
            },
        },
    });

    const onCloseModal = () => {
        closeModal();
        trackClickEvent({
            subCategory: 'MEETING_DECLINED_CLOSED',
        });
    };

    const onApprove = () => {
        declineMeeting.mutate(
            {
                eventId,
                meetingId: meeting.id,
            },
            {
                onSuccess: () => {
                    closeModal();
                    setView(View.Manage);
                },
            }
        );
        trackClickEvent({
            subCategory: 'MEETING_DECLINED',
        });
    };

    return (
        <Track>
            <ScheduleModal isOpen={isOpen} closeModal={onCloseModal}>
                <Text variant="t50" className={classes.titleModal}>
                    {`Are you sure you want to decline ${otherUser.firstName}’s meeting invitation?`}
                </Text>
                <MainButton
                    onClick={onApprove}
                    variant="fill"
                    before={declineMeeting.isLoading && <LoaderSpinner />}
                >
                    {declineMeeting.isLoading ? 'Declining' : 'Yes, Decline'}
                </MainButton>
            </ScheduleModal>
        </Track>
    );
};

const useStyles = makeStyles(
    () => ({
        titleModal: {
            marginBottom: 24,
            textAlign: 'center',
        },
    }),
    { classNamePrefix: 'meetings-decline-meeting-modal' }
);

export default DeclineMeetingModal;
